import React from 'react'
import {
    ImageRatio,
    BreakpointStretchDirection,
    ImageCrops,
    FixedRatio,
    CropBreakpoint,
    CropBreakpointSet,
    ImageCrop,
    ProcessedBreakpointCrops,
    BreakpointRatios,
    BreakpointCrops,
    ImageSource,
} from 'common/client-data-types/image-item'
import { getSrcSetValue, ImageSrcInfo } from './layouts/helpers/srcSet'
import { breakpoints } from 'component-library/__styling/settings'
import { KnownRatioToRatio } from 'publication-types'
import { logInTrackJS } from 'common/trackjs'

let globalDisableLazyLoad = false
export const disableImageLazyLoad = () => (globalDisableLazyLoad = true)
export const isLazyLoadGloballyDisabled = () => globalDisableLazyLoad

// Returns interable breakpoints
export function getIterableBreakpoints<T = any>(
    breakpointSet: CropBreakpointSet<T>
): (keyof CropBreakpointSet<T>)[] {
    return Object.keys(breakpointSet) as (CropBreakpoint | 'default')[]
}

// Returns the relevant crop ratio from image crops
export function getPlaceholderRatio(
    crops: ImageCrops,
    fixedRatio: BreakpointRatios | undefined
): BreakpointCrops<ImageRatio | undefined> | undefined {
    if (fixedRatio) {
        return mapCropBreakpoint(fixedRatio, ratio => {
            if (ratio === 'original') {
                return crops.original ? crops.original.ratio : undefined
            }
            const crop = crops[ratio]
            return crop ? crop.ratio : KnownRatioToRatio[ratio]
        })
    }

    return undefined
}

// Maps over the crop breakpoints returning an object that is the result of the cropFn only if its not null
// similar logic to the forEachBreakpoint
export function mapCropBreakpoint<T = any, R = any>(
    breakpointSet: CropBreakpointSet<T>,
    cropFn: (crop: T) => R | null | undefined
): CropBreakpointSet<R> | undefined {
    // eslint-disable-next-line
    const mapResult = {} as CropBreakpointSet<R>
    for (const breakpointString in breakpointSet) {
        if (!breakpointSet.hasOwnProperty(breakpointString)) continue
        const breakpoint = breakpointString as CropBreakpoint | 'default'
        // its guaranteed to be specified because of hasOwnProperty
        const crop = breakpointSet[breakpoint] as T
        const cropFnResult = cropFn(crop)
        if (cropFnResult !== null && cropFnResult !== undefined) {
            ;(mapResult as CropBreakpointSet<R>)[breakpoint] = cropFnResult
        }
    }
    // there is a chance its still {} here depending on the function invoking this and the cropFn logic, hence returning undefined
    return Object.keys(mapResult).length > 0 ? mapResult : undefined
}

// This iterates over a CropBreakpointSet<T> and returns a function that represents
// the given crop breakpoint and its crop value
export function forEachCropBreakpoint<T = any>(
    breakpointSet: CropBreakpointSet<T>,
    cropFn: (key: CropBreakpoint | 'default', crop: T) => void
) {
    for (const breakpointString in breakpointSet) {
        if (!breakpointSet.hasOwnProperty(breakpointString)) continue
        const breakpoint = breakpointString as CropBreakpoint | 'default'
        // its guaranteed to be specified because of hasOwnProperty
        const crop = breakpointSet[breakpoint] as T
        cropFn(breakpoint, crop)
    }
}

// Turns an array of FixedRatio's into a ImageBreakpointRatio
export function fixedRatioToBreakpointRatios(
    fixedRatios: FixedRatio | BreakpointRatios
): BreakpointRatios {
    if (typeof fixedRatios === 'string') {
        return {
            default: fixedRatios,
        }
    }
    return fixedRatios
}

/**
 * Given a fixed ratio container and an image width detects if the image needs
 * to be stretched to fit width or fit height without breaking the bounds of the container
 */
export function calculateStretchDirection(
    crops: ProcessedBreakpointCrops
): BreakpointStretchDirection {
    const returnDirection: BreakpointStretchDirection = mapCropBreakpoint<ImageCrop>(
        crops,
        crop => {
            return calculateConstraint(crop.ratio, crop.width, crop.height)
        }
    )! //marking this that it exists for typescript, we know it will.
    return returnDirection
}

// Calculate the constraint either width or height
function calculateConstraint(ratio: ImageRatio, imageWidth: number, imageHeight: number) {
    const containerRatio = Math.round((ratio.width / ratio.height + 0.00001) * 100) / 100
    const imageRatio = Math.round((imageWidth / imageHeight + 0.00001) * 100) / 100
    return imageRatio > containerRatio ? 'width' : 'height'
}

/**
 * Given an aspect ratio, we can calculate the fixed height of a square
 * to ensure the box is that ratio. We use this for our placeholders
 * @param dimensions { width: number, height: number}
 */
export const calculateIntrinsicRatio = (dimensions: ImageRatio) => {
    // Calculate the padding/height
    return `${Math.round((dimensions.height / dimensions.width) * 10000) / 100}%`
}

export function safeAttr(str: string) {
    return ('' + str) /* Forces the conversion to string. */
        .replace(/&/g, '&amp;') /* This MUST be the 1st replacement. */
        .replace(/'/g, '&apos;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/`/g, '&#96;')
}

// This returns the image crops based on the breakpoint ratios supplied
export function getCrops(
    imageCrops: ImageCrops,
    breakpointRatio: BreakpointRatios | undefined,
    useOriginalCrop: boolean
): ProcessedBreakpointCrops | undefined {
    // return the original crop, and assign a ratio equal to the first breakpoint ratio
    // this is required in cases where object-fit-contain and object-fit-cover are used
    if (useOriginalCrop && imageCrops['original']) {
        const crop = imageCrops['original']
        if (breakpointRatio) {
            const firstRatio = breakpointRatio.default
            if (firstRatio !== 'original') {
                // dont want to mutate
                return { default: { ...crop, ratio: KnownRatioToRatio[firstRatio] } }
            }
        }
        return { default: crop }
    }

    if (breakpointRatio) {
        // return the default fixedRatio crop, we only want to return the first image crop
        // as that would be the most relevant.
        const crops = mapCropBreakpoint(breakpointRatio, fixedRatios => {
            return imageCrops[fixedRatios] || null
        })

        return crops
    }

    return undefined
}

// Returns a collection of source elements, each with its own ratio, srcSet & sizes
export function getPictureSources(
    crops: ProcessedBreakpointCrops,
    imageSizes: string | undefined,
    srcInfo: ImageSrcInfo
): JSX.Element[] {
    const cropBreakpoints = getIterableBreakpoints(crops)
    const largestCropBreakpoint = getLargestCropBreakpoint(cropBreakpoints)

    return cropBreakpoints.map((breakpoint, index) => {
        const mediaQuery =
            breakpoint !== 'default'
                ? `(max-width: ${breakpoints[breakpoint] - 1}px)`
                : `(min-width: ${largestCropBreakpoint}px)`

        // this will be defined inherintly due to getting the iterable breakpoints over crops anyway
        const crop = crops[breakpoint] as ImageCrop
        const srcSetValue = getSrcSetValue(srcInfo, crop)
        const ratio = `${crop.ratio.width}:${crop.ratio.height}`
        return (
            <source
                data-ratio={ratio}
                key={index}
                media={mediaQuery}
                srcSet={srcSetValue}
                sizes={imageSizes}
            />
        )
    })
}

export function useTrackIfPicureIsNotAvailable(isUnavailable: boolean, image: ImageSource) {
    React.useEffect(() => {
        // Gets either the origional crop name, OR the first available one
        function getFirstAvailableCrop(image: ImageSource) {
            if (image.crops.original) return image.crops.original
            return image.crops[Object.keys(image.crops)[0] as keyof ImageCrops]
        }

        if (isUnavailable) {
            const reference = getFirstAvailableCrop(image)?.reference || 'unknown'
            logInTrackJS(`No appropriate crop found for image [reference: ${reference}]`)
        }
    }, [])
}

// Determines the highest breakpoint from a collection of crop breakpoints
function getLargestCropBreakpoint(cropBreakpoints: (keyof CropBreakpointSet<any>)[]): number {
    return cropBreakpoints.reduce(
        (currentHighest, breakpoint) =>
            breakpoint !== 'default' && breakpoints[breakpoint] > currentHighest
                ? breakpoints[breakpoint]
                : currentHighest,
        0
    )
}
